import { Injectable, InjectionToken } from '@angular/core';
import { FacebookLoginProvider, GoogleLoginProvider, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { environment } from '../../../environments/environment';

/**
 * Facebook prijava radi tek uz App ID iz environmenta (`facebookAppId`). Dok je prazan, prijava i
 * registracija ne nude „Nastavi s Facebookom”, a `app.config.ts` ne registrira Facebook i ne učitava
 * njegov SDK. Backend bez App ID-a i secreta ionako vraća 503 FACEBOOK_NIJE_POSTAVLJEN.
 */
export function facebookPostavljen(appId: string | null | undefined): boolean {
  return !!appId?.trim();
}

/** Nudi li se Facebook prijava (prijava i registracija); u testovima se zamjenjuje. */
export const FACEBOOK_PRIJAVA = new InjectionToken<boolean>('FACEBOOK_PRIJAVA', {
  providedIn: 'root',
  factory: () => facebookPostavljen(environment.facebookAppId),
});

/**
 * Pružatelji društvene prijave. Google bez One Tapa (`oneTapEnabled: false`): One Tap bi na svakoj
 * stranici nudio prijavu i postavljao Googleov kolačić `g_state`, a prijava Googleom ide samo gumbom
 * na `/login` i `/registracija` (stranica „Kolačići”).
 */
export function konfiguracijaDrustvenePrijave(googleClientId: string, facebookAppId: string | null | undefined): SocialAuthServiceConfig {
  return {
    autoLogin: false,
    providers: [
      {
        id: GoogleLoginProvider.PROVIDER_ID,
        provider: new GoogleLoginProvider(googleClientId, { oneTapEnabled: false }),
      },
      // Facebook samo uz App ID: bez njega gumbi nisu ponuđeni i Facebookov SDK se ne učitava.
      ...(facebookPostavljen(facebookAppId)
        ? [{
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider(facebookAppId!.trim(), {
              scope: 'email,public_profile',
              fields: 'name,email,first_name,last_name',
              version: 'v19.0',
            }),
          }]
        : []),
    ],
  };
}

/**
 * Skripte društvene prijave (Googleov `accounts.google.com/gsi/client`, Facebookov SDK) učitavaju se tek
 * kad ih stranica prijave ili registracije zatraži (`ukljuci()`), a ne na svakoj stranici.
 *
 * `SocialAuthService` nastaje rano (javna navigacija treba `AuthService`), a konfiguraciju čita kao
 * Promise (`SOCIAL_AUTH_CONFIG` u `app.config.ts`): dok `ukljuci()` nije pozvan, nijedan pružatelj se ne
 * inicijalizira i nijedna skripta ne učitava. Odjava prije toga samo odbije `signOut()` (lib još nije
 * spreman), što `AuthService.logout()` ionako ignorira.
 */
@Injectable({ providedIn: 'root' })
export class DrustvenaPrijava {
  private pokreni: () => void = () => undefined;
  private ukljucena = false;

  /** Ide u `SOCIAL_AUTH_CONFIG`; razriješi se tek na `ukljuci()`. */
  readonly konfiguracija = new Promise<SocialAuthServiceConfig>(razrijesi => {
    this.pokreni = () => razrijesi(konfiguracijaDrustvenePrijave(environment.googleClientId, environment.facebookAppId));
  });

  /** Stranica s gumbom za Google ili Facebook: učitaj skripte (jednom po učitavanju aplikacije). */
  ukljuci(): void {
    if (this.ukljucena) {
      return;
    }
    this.ukljucena = true;
    this.pokreni();
  }

  /** Je li neka stranica zatražila društvenu prijavu (za testove i provjere). */
  jeUkljucena(): boolean {
    return this.ukljucena;
  }
}
